import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import type { TmProjectStatus } from "../../api/tm";

const STATUSES: TmProjectStatus[] = ["PLANNING", "ACTIVE", "ON_HOLD", "COMPLETED", "CANCELLED"];

const STYLES: Record<TmProjectStatus, { bg: string; fg: string }> = {
  PLANNING:  { bg: "var(--tone-indigo-bg)", fg: "var(--tone-indigo)" },
  ACTIVE:    { bg: "var(--info-bg)", fg: "var(--info)" },
  ON_HOLD:   { bg: "var(--warn-bg)", fg: "var(--warn)" },
  COMPLETED: { bg: "var(--ok-bg)", fg: "var(--ok)" },
  CANCELLED: { bg: "var(--surface-muted)", fg: "var(--ink-3)" },
};

/**
 * Styled project-status picker — a status chip that opens a design-system menu
 * (coloured dot + checkmark on the current). Replaces the native <select> whose
 * popup the OS renders un-styled.
 */
export function TmProjectStatusDropdown({
  value,
  onChange,
  disabled,
}: {
  value: TmProjectStatus;
  onChange: (next: TmProjectStatus) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // A state the browser does not know is a state the server added since this bundle was built,
  // which is exactly what a reader with an open tab sees during a release. An unknown state is
  // shown plainly rather than taking the screen down with it.
  const cur = STYLES[value] ?? { bg: "var(--surface-2)", fg: "var(--ink-2)" };
  const label = (s: TmProjectStatus) => t(`tm.project.status.${s}`, { defaultValue: s });

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        title={t("tm.projects.detail.changeStatus", "Change project status")}
        style={{ ...trigger, background: cur.bg, color: cur.fg, cursor: disabled ? "default" : "pointer" }}
      >
        <span style={{ width: 6, height: 6, borderRadius: 999, background: cur.fg, flex: "none" }} />
        {label(value)}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div style={menu}>
          {STATUSES.map((s) => {
            const st = STYLES[s];
            const selected = s === value;
            return (
              <button
                key={s}
                type="button"
                className="tm-status-opt"
                onClick={() => { setOpen(false); if (s !== value) onChange(s); }}
                style={selected ? { ...opt, background: "var(--surface-2)" } : opt}
              >
                <span style={{ width: 7, height: 7, borderRadius: 999, background: st.fg, flex: "none" }} />
                <span style={{ flex: 1 }}>{label(s)}</span>
                {selected && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--brand-primary)"
                       strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const trigger: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 8,
  height: 32, padding: "0 12px", borderRadius: 999,
  border: "1px solid var(--border)", fontSize: 12, fontWeight: 600,
};
const menu: CSSProperties = {
  position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 60,
  minWidth: 190, padding: 6, background: "var(--surface)",
  border: "1px solid var(--border)", borderRadius: 10,
  boxShadow: "var(--shadow-2)",
  display: "flex", flexDirection: "column", gap: 2,
};
const opt: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8,
  padding: "7px 10px", borderRadius: 7, border: "none",
  color: "var(--ink)", fontSize: 13, cursor: "pointer", textAlign: "left", width: "100%",
};
